import React, {useEffect, useMemo} from 'react';
import DashboardLayout from "../../layouts/DashboardLayout";
import {useTranslation} from "react-i18next";
import StepPanel from "../../components/registration/StepPanel";
import useRegistrationFormStore from "../../store/registration-form";
import {useMutation, useQuery} from "react-query";
import {apollo} from "../../utils/apollo";
import {GET_MERCHANT_REGISTRATION_FIELDS, GET_REGISTRATION_COUNTRIES, UPDATE_REGISTRATION} from "../../utils/graphql";
import useAuthStore from "../../store/auth";
import RegistrationStringField from "../../components/registration/fields/RegistrationStringField";
import RegistrationFileField from "../../components/registration/fields/RegistrationFileField";
import RegistrationCountryField from "../../components/registration/fields/RegistrationCountryField";
import RegistrationBooleanField from "../../components/registration/fields/RegistrationBooleanField";
import {toast} from "react-toastify";
import {cn} from "../../utils/style";
import {BuildingLibraryIcon, DocumentTextIcon, UserIcon} from '@heroicons/react/24/outline';
import Button from "../../components/Button";
import useModalStore from "../../store/modal";
import ConfirmationModal from "../../components/ConfirmationModal";

import Loading from "../../components/Loading";
import {Navigate} from "react-router-dom";

function Registration() {
    const {t} = useTranslation();
    const {
        step, setStep, credentials, setCredentials, dynamicFormValue, setDynamicFormValue,
        setDynamicFormValues,
    } = useRegistrationFormStore()
    const {user, merchant, roles, permissions} = useAuthStore();
    const {setModal, clearModal, otpValue, otpRequired, getOtpValue, isOtpRequired, hideModal} = useModalStore();

    // const fetchRegistration = useQuery({
    // 	queryKey: ['registration'],
    // 	queryFn: () => http.get("/merchant/registration", {
    // 		params: {}
    // 	}),
    // 	keepPreviousData: false
    // });
    // const records = fetchRegistration?.data?.data || null;
    const fetchCountries = useQuery({
        queryKey: 'registration_countries',
        queryFn: async () => {
            const {data} = await apollo.query({
                query: GET_REGISTRATION_COUNTRIES,
                variables: {},
            });
            return data?.getRegistrationCountries;
        },
        refetchOnWindowFocus: false
    });

    const fetchFields = useQuery({
        queryKey: 'merchant_registration_fields',
        queryFn: async () => {
            const {data} = await apollo.query({
                query: GET_MERCHANT_REGISTRATION_FIELDS,
                variables: {
                    country: merchant?.country
                },
            });
            return data;
        },
        enabled: !!merchant?.country,
        refetchOnWindowFocus: false
    });

    const fields = useMemo(() => {
        return fetchFields?.data?.getRegistrationForm || null;
    }, [fetchFields]);

    useEffect(() => {
        const list = fetchFields?.data?.getMerchantRegistrationFields || null;
        if (list && !Object.keys(dynamicFormValue).length) {
            let dynValues = {};
            list?.forEach(item => {
                const key = item?.fieldDefinition?.fieldName || null;
                const val = item?.fieldValue || null;
                if (key && val) dynValues[key] = val;
            })
            if (Object.keys(dynValues).length) {
                setDynamicFormValues(dynValues);
            }
        }
    }, [fetchFields]);

    const updateRegistrationMutation = useMutation({
        mutationFn: async () => {
            const {data} = await apollo.mutate({
                mutation: UPDATE_REGISTRATION,
                variables: {
                    input: {
                        fields: JSON.stringify({country: merchant?.country, ...dynamicFormValue})
                    }
                },
            });
            return data?.updateRegistration;
        },
        onSuccess: (data) => {
            if (data && data.success) {
                toast.success(t('registration.success_message') || 'Registration has been updated successfully.');
            } else if (data && data.message) {
                toast.error(data.message)
            } else {
                toast.error(t('registration.check_submitted_info') || 'Please check the submitted information again!')
            }
        },
        // onError: ((error) => {
        //     if (error.graphQLErrors) {
        //         error.graphQLErrors.map((item) => console.log(item));
        //         toast.error('Something went wrong!')
        //     }
        // })
    })

    const hasAccess = roles?.includes("merchant") || permissions?.includes("sub_user_with_full_merchant_permissions");
    if (!hasAccess) {
        return <Navigate to="/dashboard" replace />;
    }

    const submitRegistrationForm = async (e) => {
        e.preventDefault();
        updateRegistrationMutation.mutate();
        // setModal({
        //     isVisible: true,
        //     title: t('registration.confirm_title'),
        //     description: t('registration.confirm_description'),
        //     otpRequired: false,
        //     confirmText: t('registration.submit'),
        //     onConfirm: () => {
        //         // if (isOtpRequired() && !getOtpValue()) {
        //         //     toast.error('Please enter OTP to confirm action.');
        //         //     return;
        //         // }
        //         updateRegistrationMutation.mutate();
        //         // hideModal();
        //     },
        //     onCancel: () => {
        //         hideModal();
        //     },
        // });
    }

    return (
        <DashboardLayout>
            <ConfirmationModal/>
            <div className="mb-10">
                <div className="flex items-center gap-4 mb-2">
                    <div className="p-3 rounded-2xl bg-white shadow-sm border border-gray-100 text-[#0b6a8c]">
                        <UserIcon className="h-7 w-7"/>
                    </div>
                    <div>
                        <h2 className="text-3xl font-extrabold text-gray-900 tracking-tight">{t('registration.page_title') || 'Merchant Registration'}</h2>
                        <p className="text-gray-500 font-medium">{t('registration.page_description') || 'Complete the registration process to activate your merchant account.'}</p>
                    </div>
                </div>
            </div>

            {(fetchFields.isLoading || fetchCountries.isLoading) && (
                <div className="bg-white/60 backdrop-blur-md rounded-[2.5rem] shadow-sm border border-gray-100 p-20 text-center">
                    <Loading message={t('registration.loading_title') || 'Loading Registration Data'} fullHeight={false} />
                </div>
            )}

            {(fetchFields.isSuccess && fetchCountries) && (
                <div className="bg-white/60 backdrop-blur-md rounded-[2.5rem] shadow-sm border border-gray-100 mb-8 overflow-hidden">
                    <div className="px-8 py-5 border-b border-gray-100/50">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="p-2.5 rounded-2xl bg-cyan-50 text-[#0b6a8c]">
                                    <DocumentTextIcon className="h-5 w-5"/>
                                </div>
                                <h3 className="text-lg font-bold text-gray-900">{t('registration.steps.title') || 'Registration Steps'}</h3>
                            </div>
                        </div>
                    </div>
                    <div className="p-8">
                        <StepPanel step={step + 1} onClick={value => setStep(value - 1)}/>
                    </div>
                </div>
            )}

            {(fetchFields.isSuccess && fetchCountries) && (
                <div>
                    <div className="bg-white/40 backdrop-blur-md rounded-[2.5rem] shadow-sm border border-gray-100 overflow-hidden mb-8">
                        <div className="px-8 py-5 border-b border-gray-100/50">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="p-2.5 rounded-2xl bg-cyan-50 text-[#0b6a8c]">
                                        {step === 0 && <UserIcon className="h-5 w-5"/>}
                                        {step === 1 && <BuildingLibraryIcon className="h-5 w-5"/>}
                                        {step === 2 && <DocumentTextIcon className="h-5 w-5"/>}
                                    </div>
                                    <h3 className="text-lg font-bold text-gray-900">
                                        {step === 0 && (t('registration.steps.personal_info'))}
                                        {step === 1 && (t('registration.steps.business_info'))}
                                        {step === 2 && (t('registration.steps.documents'))}
                                    </h3>
                                </div>
                            </div>
                        </div>
                        <div className="p-8">
                            <div className="bg-cyan-50/50 text-[#0b6a8c] p-5 rounded-3xl border border-cyan-100/50 mb-8 font-medium text-sm">
                                {step === 0 && (
                                    <p>{t('registration.steps.personal_info_description')}</p>
                                )}
                                {step === 1 && (
                                    <p>{t('registration.steps.business_info_description')}</p>
                                )}
                                {step === 2 && (
                                    <p>{t('registration.steps.documents_description')}</p>
                                )}
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                {fields?.map(field => {
                                    if (!field || step + 1 !== field.displayOnSection) return null;

                                    const renderField = () => {
                                        if (field.fieldType === 'string')
                                            return <RegistrationStringField key={field.id} field={field}
                                                                            show={true}
                                                                            get={dynamicFormValue}
                                                                            set={setDynamicFormValue}/>;
                                        if (field.fieldType === 'file')
                                            return <RegistrationFileField key={field.id} field={field}
                                                                          show={true}
                                                                          get={dynamicFormValue}
                                                                          set={setDynamicFormValue}/>;
                                        if (field.fieldType === 'country')
                                            return <RegistrationCountryField key={field.id} field={field}
                                                                             show={true}
                                                                             get={merchant?.country}
                                                                             set={null} readOnly={true}
                                                                             countries={fetchCountries?.data}/>;
                                        if (field.fieldType === 'boolean')
                                            return <RegistrationBooleanField key={field.id} field={field}
                                                                             show={true}
                                                                             get={dynamicFormValue}
                                                                             set={setDynamicFormValue}/>;
                                        return null;
                                    };

                                    return (
                                        <div key={field.id}
                                             className="bg-white p-6 rounded-[2rem] shadow-sm border border-gray-50 hover:shadow-md transition-all">
                                            {renderField()}
                                            {field.description && (
                                                <p className="text-xs font-medium text-gray-400 mt-3">{field.description}</p>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    <div className="mt-8 flex flex-wrap items-center gap-4">
                        <Button
                            secondary={true}
                            className={cn("rounded-2xl px-8 shadow-sm", {
                                'opacity-50 cursor-not-allowed': step === 0
                            })}
                            onClick={() => step > 0 && setStep(step - 1)}
                            disabled={step === 0}
                        >
                            {t('registration.previous') || 'Previous'}
                        </Button>

                        {step < 2 ? (
                            <Button
                                className="rounded-2xl px-8 shadow-sm"
                                onClick={() => setStep(step + 1)}
                            >
                                {t('registration.next') || 'Next'}
                            </Button>
                        ) : (
                            <Button
                                className="rounded-2xl px-10 shadow-lg shadow-blue-600/20"
                                onClick={submitRegistrationForm}
                            >
                                {t('registration.submit') || 'Update Registration'}
                            </Button>
                        )}
                    </div>
                </div>
            )}

        </DashboardLayout>
    );
}

export default Registration;
